//Users/aminb101/iuhimporter/iuh-importer/frontend/src/pages/ProductAnalyticsPage.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../services/api'; // Use centralized API service
import { Spinner } from '../components/common/Spinner';
import ProductPerformanceLineChart from '../components/charts/ProductPerformanceLineChart';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const formatNumber = (num, options = {}) => {
    if (num == null || isNaN(num)) return 'N/A';
    return num.toLocaleString(undefined, options);
};

const ProductAnalyticsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storeId, setStoreId] = useState(null);
    const [dateRange, setDateRange] = useState('30'); // Default is 30 days
    const [chartData, setChartData] = useState(null);
    const [chartLoading, setChartLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPageTop, setCurrentPageTop] = useState(1);
    const [itemsPerPageTop] = useState(20);
    const [currentPageUnder, setCurrentPageUnder] = useState(1);
    const [itemsPerPageUnder] = useState(20);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [bulkActionMenuOpen, setBulkActionMenuOpen] = useState(false);
    const [sortConfigTop, setSortConfigTop] = useState({ key: 'views', direction: 'descending', avgCtr: 'ctr' });
    const [sortConfigUnder, setSortConfigUnder] = useState({ key: 'ctr', direction: 'ascending' });
    const [syncing, setSyncing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');


    // Thresholds for underperforming products
    const UNDERPERFORMING_VIEWS_THRESHOLD = 20;
    const UNDERPERFORMING_CTR_THRESHOLD = 0.01;

    // More lenient thresholds for top products
    const TOP_PERFORMING_VIEWS_THRESHOLD = 10; // Reduced from 500
    const TOP_PERFORMING_CTR_THRESHOLD = 0.005; // Reduced from 0.03

    console.log('Thresholds - Underperforming:', { views: UNDERPERFORMING_VIEWS_THRESHOLD, ctr: UNDERPERFORMING_CTR_THRESHOLD });
    console.log('Thresholds - Top Performing:', { views: TOP_PERFORMING_VIEWS_THRESHOLD, ctr: TOP_PERFORMING_CTR_THRESHOLD });

    // Use the dateRange directly as the period key
    const analyticsPeriodKey = dateRange;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const storeRes = await api.get('/api/stores');
            if (!storeRes.data || storeRes.data.length === 0) {
                setProducts([]);
                setStoreId(null);
                console.warn('No stores found.');
                return;
            }

            const currentStoreId = storeRes.data[0].id;
            setStoreId(currentStoreId);
            console.log(`Using store ID: ${currentStoreId}`);

            // Fetch with the selected date range
            const analyticsRes = await api.get(`/api/analytics/unified/${currentStoreId}?range=${dateRange}`); // 
            if (Array.isArray(analyticsRes.data)) {

                console.log(`Fetched ${analyticsRes.data.length} products for ${dateRange} days.`);
                setProducts(analyticsRes.data);
            } else {
                console.warn('Analytics response is not an array:', analyticsRes.data);
                setProducts([]);
            }
        } catch (err) {
            console.error('Error fetching initial data:', err);
            setProducts([]);
            setStoreId(null);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);


    useEffect(() => {
        fetchData();
    }, [fetchData]);


    useEffect(() => {
        console.log(' Total products loaded:', products.length);
    }, [products]);


    useEffect(() => {
        const fetchChartData = async () => {
            if (!storeId) {
                setChartData(null);
                setChartLoading(false);
                return;
            }

            const apiUrl = `/api/analytics/timeseries/${storeId}?range=${dateRange}`;
            setChartLoading(true);
            setChartData(null);

            try {
                const res = await api.get(apiUrl);
                console.log("Chart API Response:", res.data);

                if (res.data && Array.isArray(res.data.labels) && Array.isArray(res.data.values)) {
                    // Transformation logic START
                    const transformedData = {
                        labels: res.data.labels,
                        datasets: [
                            {
                                label: "Product Views",
                                data: res.data.values,
                                fill: false,
                                borderColor: "rgb(75, 192, 192)",
                                tension: 0.1,
                            },
                        ],
                    };
                    setChartData(transformedData);
                    // Transformation logic END
                } else {
                    console.warn('Invalid chart data format received:', res.data);
                    setChartData(null);
                }
            } catch (err) {
                console.error('Error fetching chart data:', err.response?.data || err.message || err);
                setChartData(null);
            } finally {
                setChartLoading(false);
            }
        };

        fetchChartData();
    }, [dateRange, storeId]);

    useEffect(() => {
        console.log("chartData state:", chartData);
    }, [chartData]);

    const underperformers = useMemo(() => {
        if (!products || products.length === 0) return [];
        const filtered = products.filter(p => {
            const metrics = p.analytics?.[analyticsPeriodKey];
            if (!metrics) return false;

            const views = parseInt(metrics.views || "0", 10);
            const ctr = parseFloat(metrics.ctr || 0);
            return views < UNDERPERFORMING_VIEWS_THRESHOLD && ctr < UNDERPERFORMING_CTR_THRESHOLD;
        });
        filtered.sort((a, b) => {
            const ctrA = parseFloat(a.analytics?.[analyticsPeriodKey]?.ctr || 0);
            const ctrB = parseFloat(b.analytics?.[analyticsPeriodKey]?.ctr || 0);
            return ctrA - ctrB;
        });
        console.log(`Identified ${filtered.length} underperforming products for ${analyticsPeriodKey}`);
        return filtered;
    }, [products, analyticsPeriodKey]);

    const topProducts = useMemo(() => {
        if (!products || products.length === 0) {
            console.log('No products available for filtering');
            return [];
        }

        const filtered = products.filter(p => {
            const metrics = p.analytics?.[analyticsPeriodKey];
            if (!metrics) {
                console.log('No metrics for product:', p.title);
                return false;
            }

            const views = parseInt(metrics.views || "0", 10);
            const ctr = parseFloat(metrics.ctr || 0);
            const isTopPerformer = views >= TOP_PERFORMING_VIEWS_THRESHOLD && ctr >= TOP_PERFORMING_CTR_THRESHOLD;

            if (isTopPerformer) {
                console.log('Top product found:', {
                    title: p.title,
                    views,
                    ctr,
                    thresholds: { views: TOP_PERFORMING_VIEWS_THRESHOLD, ctr: TOP_PERFORMING_CTR_THRESHOLD }
                });
            }

            return isTopPerformer;
        });
        filtered.sort((a, b) => {
            const viewsA = parseInt(a.analytics?.[analyticsPeriodKey]?.views || "0", 10);
            const viewsB = parseInt(b.analytics?.[analyticsPeriodKey]?.views || "0", 10);
            return viewsB - viewsA;
        });
        console.log(`Identified ${filtered.length} top performing products for ${analyticsPeriodKey}`, {
            sampleMetrics: filtered.slice(0, 3).map(p => ({
                title: p.title,
                views: p.analytics?.[analyticsPeriodKey]?.views,
                ctr: p.analytics?.[analyticsPeriodKey]?.ctr
            }))
        });
        return filtered;
    }, [products, analyticsPeriodKey]);

    const overviewStats = useMemo(() => {
        if (!products || products.length === 0) {
            return {
                totalProducts: 0,
                totalTraffic: 0,
                averageCtr: 0,
                underperformingCount: 0,
            };
        }

        let totalTrafficSum = 0;
        let totalCtrSum = 0;
        let productsWithCtr = 0;
        let underperformingCount = 0;

        products.forEach(p => {
            const metrics = p.analytics?.[analyticsPeriodKey];
            if (metrics) {
                const views = parseInt(metrics.views || "0", 10);
                const ctr = parseFloat(metrics.ctr || 0);

                totalTrafficSum += views;

                if (metrics.ctr !== null && metrics.ctr !== undefined) {
                    totalCtrSum += ctr;
                    productsWithCtr++;
                }

                const isUnderperforming = views < UNDERPERFORMING_VIEWS_THRESHOLD && ctr < UNDERPERFORMING_CTR_THRESHOLD;
                if (isUnderperforming) {
                    underperformingCount++;
                }
            }
        });

        const averageCtr = productsWithCtr > 0 ? (totalCtrSum / productsWithCtr) : 0;

        return {
            totalProducts: products.length,
            totalTraffic: totalTrafficSum,
            averageCtr: averageCtr,
            underperformingCount: underperformingCount,
        };
    }, [products, analyticsPeriodKey]);


    // Add sortIndicatorTop function here
    const sortIndicatorTop = (key) => {
        if (sortConfigTop.key !== key) return null;
        return sortConfigTop.direction === 'ascending' ? ' ↑' : ' ↓';
    };

    const requestSortTop = (key) => {
        let direction = 'ascending';
        if (sortConfigTop.key === key && sortConfigTop.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfigTop({ key, direction });
        setCurrentPageTop(1);
    };

    // Add requestSortUnder function here
    const requestSortUnder = (key) => {
        let direction = 'ascending';
        if (sortConfigUnder.key === key && sortConfigUnder.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfigUnder({ key, direction });
        setCurrentPageUnder(1);
    };

    // Add sortIndicatorUnder function here
    const sortIndicatorUnder = (key) => {
        if (sortConfigUnder.key !== key) return null;
        return sortConfigUnder.direction === 'ascending' ? ' ↑' : ' ↓';
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };


    const sortedProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];

        let filteredProducts = [...products];

        if (searchQuery.trim() !== '') {
            filteredProducts = filteredProducts.filter(p =>
                p.title?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (sortConfig.key) {
            filteredProducts.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.key) {
                    case 'product':
                        aValue = a.title?.replace(/^"|"$/g, '') || '';
                        bValue = b.title?.replace(/^"|"$/g, '') || '';
                        break;
                    case 'images':
                        aValue = a.images || 0;
                        bValue = b.images || 0;
                        break;
                    case 'altText':
                        aValue = a.altTextCount || 0;
                        bValue = b.altTextCount || 0;
                        break;
                    case 'seoScore':
                        aValue = a.seoScore || 0;
                        bValue = b.seoScore || 0;
                        break;
                    case 'traffic':
                        aValue = parseInt(a.analytics?.[analyticsPeriodKey]?.views || "0", 10);
                        bValue = parseInt(b.analytics?.[analyticsPeriodKey]?.views || "0", 10);
                        break;
                    case 'ctr':
                        aValue = parseFloat(a.analytics?.[analyticsPeriodKey]?.ctr || 0);
                        bValue = parseFloat(b.analytics?.[analyticsPeriodKey]?.ctr || 0);
                        break;
                    case 'keywords':
                        aValue = a.analytics?.[analyticsPeriodKey]?.keywords?.length || 0;
                        bValue = b.analytics?.[analyticsPeriodKey]?.keywords?.length || 0;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return filteredProducts;
    }, [products, sortConfig, analyticsPeriodKey, searchQuery]);

    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedProducts.slice(startIndex, endIndex);
    }, [sortedProducts, currentPage, itemsPerPage]);

    const sortedTopProducts = useMemo(() => {
        return [...topProducts].sort((a, b) => {
            const metricA = a.analytics?.[analyticsPeriodKey];
            const metricB = b.analytics?.[analyticsPeriodKey];
            const aValue = sortConfigTop.key === 'ctr'
                ? parseFloat(metricA?.ctr || 0)
                : parseInt(metricA?.views || 0);
            const bValue = sortConfigTop.key === 'ctr'
                ? parseFloat(metricB?.ctr || 0)
                : parseInt(metricB?.views || 0);
            return sortConfigTop.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        });
    }, [topProducts, sortConfigTop, analyticsPeriodKey]);

    const paginatedTopProducts = useMemo(() => {
        const startIndex = (currentPageTop - 1) * itemsPerPageTop;
        return sortedTopProducts.slice(startIndex, startIndex + itemsPerPageTop);
    }, [sortedTopProducts, currentPageTop, itemsPerPageTop]);


    const sortedUnderperformers = useMemo(() => {
        return [...underperformers].sort((a, b) => {
            const metricA = a.analytics?.[analyticsPeriodKey];
            const metricB = b.analytics?.[analyticsPeriodKey];

            let aValue, bValue;

            if (sortConfigUnder.key === 'ctr') {
                aValue = parseFloat(metricA?.ctr || 0);
                bValue = parseFloat(metricB?.ctr || 0);
            } else if (sortConfigUnder.key === 'avgCtr') {
                aValue = parseFloat(a.analytics?.[analyticsPeriodKey]?.ctr || 0);
                bValue = parseFloat(b.analytics?.[analyticsPeriodKey]?.ctr || 0);
            } else {
                return 0;
            }


            return sortConfigUnder.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        });
    }, [underperformers, sortConfigUnder, analyticsPeriodKey]);

    const paginatedUnderperformers = useMemo(() => {
        const startIndex = (currentPageUnder - 1) * itemsPerPageUnder;
        return sortedUnderperformers.slice(startIndex, startIndex + itemsPerPageUnder);
    }, [sortedUnderperformers, currentPageUnder, itemsPerPageUnder]);

    const handleSyncProducts = async (storeIdToUse) => {
        if (!storeIdToUse) {
            alert("Please select a store first.");
            return;
        }

        setSyncing(true);
        try {
            console.log(` Syncing products for store ${storeIdToUse}`);
            const res = await api.get(`/api/shopify/sync-products/${storeIdToUse}`);
            if (res.status === 200) {
                alert(' Product sync complete.');
                fetchData(); // Re-fetch analytics data
            } else {
                alert(' Product sync failed.');
            }
        } catch (err) {
            console.error('Error syncing products:', err.response?.data || err.message);
            alert(' Failed to sync products. Check console.');
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncRecentProducts = async (storeIdToUse) => {
        if (!storeIdToUse) {
            alert("Please select a store first.");
            return;
        }

        if (!window.confirm('This will sync only products updated in the last 10 days. Continue?')) {
            return;
        }

        setSyncing(true);
        try {
            console.log(` Syncing recent products for store ${storeIdToUse}`);
            const res = await api.get(`/api/shopify/sync-recent-products/${storeIdToUse}`);
            if (res.status === 200) {
                alert(' Recent product sync complete.');
                fetchData(); // Re-fetch analytics data
            } else {
                alert(' Recent product sync failed.');
            }
        } catch (err) {
            console.error('Error syncing recent products:', err.response?.data || err.message);
            alert(' Failed to sync recent products. Check console.');
        } finally {
            setSyncing(false);
        }
    };

    const handleNextPageTop = useCallback(() => {
        setCurrentPageTop(prev => Math.min(prev + 1, Math.ceil(topProducts.length / itemsPerPageTop)));
    }, [topProducts.length, itemsPerPageTop]);

    const handlePrevPageTop = useCallback(() => {
        setCurrentPageTop(prev => Math.max(prev - 1, 1));
    }, []);

    //const handleNextPageUnder = useCallback(() => {
    //    setCurrentPageUnder(prev => Math.min(prev + 1, Math.ceil(underperformers.length / itemsPerPageUnder)));
    //}, [underperformers.length, itemsPerPageUnder]);

    //const handlePrevPageUnder = useCallback(() => {
    //    setCurrentPageUnder(prev => Math.max(prev - 1, 1));
    //}, []);

    useEffect(() => {
        setCurrentPage(1);
        setCurrentPageTop(1);
        setCurrentPageUnder(1);
        console.log('Pagination reset due to data or period change.');
    }, [products.length, analyticsPeriodKey]);

    const toggleProductSelection = useCallback((productShopifyId) => {
        setSelectedProducts(prev =>
            prev.includes(productShopifyId)
                ? prev.filter(id => id !== productShopifyId)
                : [...prev, productShopifyId]
        );
    }, []);

    const toggleSelectAll = useCallback(() => {
        console.log(' Paginated products:', paginatedProducts);
        const currentVisibleProductIds = paginatedProducts.map(p => p.shopifyId).filter(Boolean);

        const allVisibleSelected = currentVisibleProductIds.length > 0 && currentVisibleProductIds.every(id => selectedProducts.includes(id));

        if (allVisibleSelected) {
            setSelectedProducts(prev => prev.filter(id => !currentVisibleProductIds.includes(id)));
        } else {
            setSelectedProducts(prev => [...new Set([...prev, ...currentVisibleProductIds])]);
        }
    }, [paginatedProducts, selectedProducts]);

    const toggleBulkActionMenu = useCallback(() => {
        setBulkActionMenuOpen(prev => !prev);
    }, [setBulkActionMenuOpen]);


    const handleBulkAction = async (action) => {
        if (selectedProducts.length === 0 || !storeId) {
            toast.warning("Please select at least one product");
            return;
        }

        console.log(`Performing ${action} on ${selectedProducts.length} products for store ${storeId}`);
        console.log('IDs being sent:', selectedProducts);

        const endpointMap = {
            'Categorize Products': '/api/enrich/categorize',
            'Enrich Metafields': '/api/enrich/metafields',
            'Enrich SEO Metadata': '/api/enrich/ai',
            'Enrich Alt Image Text': '/api/enrich/alt-text',
            'Enrich Everything': '/api/enrich/all',
            'Compress Images': '/api/enrich/compress-images',
            'Translate Variant Images': '/api/process-variant-images',
            'Enhance Images with AI': '/api/enrich/generate-ai-image',
        };

        const endpoint = endpointMap[action];
        if (!endpoint) {
            console.error("Invalid bulk action:", action);
            toast.error("Invalid bulk action selected.");
            return;
        }

        // Special handling for image enhancement

        // Special handling for AI image enhancement
        if (action === 'Enhance Images with AI') {
            try {
                setLoading(true);
                const toastId = toast.loading(
                    `Enhancing images for ${selectedProducts.length} products...`,
                    {
                        autoClose: false,
                        closeOnClick: false,
                        closeButton: true
                    }
                );

                // Show confirmation for large batches
                if (selectedProducts.length > 10) {
                    const shouldContinue = window.confirm(
                        `You are about to enhance images for ${selectedProducts.length} products. This may take a while. Continue?`
                    );
                    if (!shouldContinue) {
                        toast.dismiss(toastId);
                        setLoading(false);
                        return;
                    }
                }

                const response = await api.post('/api/enrich/generate-ai-image', {
                    productIds: selectedProducts,
                    storeId
                }, {
                    timeout: 300000 // 5 minute timeout
                });

                const { results = [] } = response.data;
                const successCount = results.filter(r => r.success).length;
                const failCount = results.length - successCount;

                // Update UI with results
                toast.dismiss(toastId);
                if (successCount > 0) {
                    toast.success(
                        `Successfully enhanced images for ${successCount} product(s)`,
                        { autoClose: 5000 }
                    );
                }
                if (failCount > 0) {
                    toast.error(
                        `Failed to enhance images for ${failCount} product(s)`,
                        { autoClose: 5000 }
                    );
                }

                // Log detailed results
                if (failCount > 0) {
                    const failedProducts = results.filter(r => !r.success);
                    console.error('Failed products:', failedProducts);
                    // Optionally show a modal with failed products
                }

                // Refresh data
                await fetchData();
                setSelectedProducts([]);

            } catch (error) {
                console.error('Error in bulk image enhancement:', error);
                const errorMessage = error.response?.data?.message ||
                    error.message ||
                    'Failed to enhance images';

                toast.error(`Error: ${errorMessage}`, {
                    autoClose: 5000,
                    closeOnClick: true
                });

                if (error.code === 'ECONNABORTED') {
                    toast.warning('Request timed out. Some images may still be processing in the background.');
                }
            } finally {
                setLoading(false);
                setBulkActionMenuOpen(false);
            }
            return;
        }

        // Handle other bulk actions
        try {
            setLoading(true);
            const toastId = toast.loading(
                `Processing ${selectedProducts.length} products...`,
                { autoClose: false }
            );

            const res = await api.post(endpoint, {
                storeId,
                productIds: selectedProducts,
            });

            toast.dismiss(toastId);

            if (res.data && res.data.success) {
                toast.success(`${action} initiated successfully for ${selectedProducts.length} products.`);
                setSelectedProducts([]);
                await fetchData();
            } else {
                toast.error(`${action} failed: ${res.data?.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error(`Error during ${action}:`, error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            toast.error(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
            setBulkActionMenuOpen(false);
        }
    };


    // Close bulk action menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const menuButton = document.getElementById('bulk-actions-menu');
            const menuContainer = document.getElementById('bulk-actions-dropdown');

            if (
                bulkActionMenuOpen &&
                menuButton && !menuButton.contains(event.target) &&
                menuContainer && !menuContainer.contains(event.target)
            ) {
                setBulkActionMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [bulkActionMenuOpen]);

    // Get sort indicator arrow
    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    };

    const handleViewKeywords = (product, currentPeriod) => {
        // Implement your logic to view keywords, maybe a modal or console log
        console.log(`View keywords clicked for product: ${product.title} in period: ${currentPeriod}`);
        console.log(product.analytics?.[currentPeriod]?.keywords);
    };


    // --- Loading State ---
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner />
                <span className="ml-3 text-gray-500">Loading product analytics...</span>
            </div>
        );
    }

    // --- No Products State ---
    if (!loading && products.length === 0) {
        return (
            <div className="text-center text-gray-500 py-10">
                <p className="text-lg mb-2">No product data found.</p>
                <p className="mb-4">Try syncing your Shopify store products below:</p>
                <div className="mt-4">
                    <button
                        onClick={() => handleSyncProducts(storeId)}
                        disabled={!storeId || syncing}
                        className="bg-indigo-600 text-white px-5 py-2 rounded hover:bg-indigo-700 transition disabled:opacity-50"
                    >
                        {syncing ? 'Syncing Products...' : 'Sync Products'}
                    </button>
                </div>

            </div>
        );
    }



    // --- Render Page Content ---

    <button
        onClick={() => handleSyncProducts(storeId)}
        disabled={!storeId || syncing}
        className="bg-indigo-600 text-white px-5 py-2 rounded hover:bg-indigo-700 transition disabled:opacity-50"
    >
        {syncing ? 'Syncing Products...' : 'Sync Products'}
    </button>


    return (
        <>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Product Analytics</h2>
                <p className="text-gray-600 mt-1">Track traffic, keywords, and performance for all your products</p>
            </div>

            <div className="flex justify-end items-center mb-4">
                <label className="mr-2 text-sm text-gray-600">Show data for:</label>
                <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="365">Last 365 days</option>
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Products</p>
                            <h3 className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(overviewStats.totalProducts)}</h3>
                        </div>
                        <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <i className="fas fa-box text-indigo-600 text-xl"></i>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Traffic ({dateRange}d)</p>
                            <h3 className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(overviewStats.totalTraffic)}</h3>
                        </div>
                        <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                            <i className="fas fa-users text-green-600 text-xl"></i>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Avg. Click-Through Rate</p>
                            <h3 className="text-2xl font-bold text-gray-800 mt-1">
                                {formatNumber((overviewStats?.averageCtr || 0) * 100, { maximumFractionDigits: 1 })}%
                            </h3>
                        </div>
                        <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <i className="fas fa-mouse-pointer text-blue-600 text-xl"></i>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Underperforming Products</p>
                            <h3 className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(overviewStats.underperformingCount)}</h3>
                        </div>
                        <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                            <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-800">Product Performance Trend</h3>
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={chartLoading}
                    >
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="365">Last 365 days</option>
                    </select>
                </div>

                {chartLoading ? (
                    <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded border border-dashed border-gray-300">
                        <Spinner />
                        <span className="ml-3 text-gray-500">Loading chart data...</span>
                    </div>
                ) : !chartData ? (
                    <div className="w-full h-64 flex items-center justify-center bg-gray-50 rounded border border-dashed border-gray-300">
                        <div className="text-center">
                            <i className="fas fa-exclamation-triangle text-xl text-yellow-500 mb-2"></i>
                            <p className="text-gray-500">Could not load performance data.</p>
                            <p className="text-xs text-gray-400 mt-1">Backend might be unavailable or data format incorrect. Check console.</p>
                        </div>
                    </div>
                ) : (
                    <ProductPerformanceLineChart
                        chartData={chartData}
                        title={`Product Views - Last ${dateRange} Days`}
                    />
                )}
            </div>

            {/* Product Optimization Table goes OUTSIDE here */}

            <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-medium text-gray-800">Product Optimization Overview</h3>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border border-gray-300 rounded-md pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm w-60"
                            />
                            <i className="fas fa-search absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                        <button
                            onClick={() => handleSyncRecentProducts(storeId)}
                            disabled={syncing || !storeId}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${(syncing || !storeId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {syncing ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Syncing...
                                </>
                            ) : (
                                <>
                                    <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Sync Last 10 Days
                                </>
                            )}
                        </button>
                    </div>

                    {selectedProducts.length > 0 && (
                        <div className="relative inline-block text-left">
                            <div>
                                <button
                                    type="button"
                                    id="bulk-actions-menu"
                                    className="inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                    aria-expanded={bulkActionMenuOpen}
                                    aria-haspopup="true"
                                    onClick={toggleBulkActionMenu}
                                >
                                    Bulk Actions ({selectedProducts.length})
                                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>

                            {bulkActionMenuOpen && (
                                <div
                                    id="bulk-actions-dropdown"
                                    className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="bulk-actions-menu"
                                >
                                    <div className="py-1" role="none">
                                        <button onClick={() => handleBulkAction("Enrich Metafields")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Metafields</button>
                                        <button onClick={() => handleBulkAction("Enrich SEO Metadata")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich SEO Metadata</button>
                                        <button onClick={() => handleBulkAction("Enrich Alt Image Text")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Alt Image Text</button>
                                        <button onClick={() => handleBulkAction("Enrich Everything")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Everything</button>
                                        <button onClick={() => handleBulkAction("Categorize Products")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Categorize Products</button>
                                        <button onClick={() => handleBulkAction("Compress Images")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Compress Images</button>
                                        <button onClick={() => handleBulkAction("Translate Variant Images")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Translate Variant Images</button>
                                        <div className="border-t border-gray-200 my-1"></div>
                                        <button
                                            onClick={() => handleBulkAction("Enhance Images with AI")}
                                            className="block w-full text-left px-4 py-2 text-sm text-purple-700 hover:bg-purple-50 hover:text-purple-900 font-medium flex items-center"
                                            role="menuitem"
                                        >
                                            <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Enhance Images with AI
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                                            checked={
                                                paginatedProducts.length > 0 &&
                                                paginatedProducts.every(p => selectedProducts.includes(p.shopifyId))
                                            }
                                            onChange={toggleSelectAll}
                                        />
                                        <button
                                            onClick={() => requestSort('product')}
                                            className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                        >
                                            <span className="group-hover:text-indigo-600 transition-colors">Product</span>
                                            <span className="text-indigo-500 ml-1">{getSortIndicator('product')}</span>
                                        </button>
                                    </div>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('images')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">Images</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('images')}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('altText')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">Alt Text</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('altText')}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('seoScore')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">SEO Score</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('seoScore')}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('traffic')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">Traffic</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('traffic')}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('ctr')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">CTR</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('ctr')}</span>
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    <button
                                        onClick={() => requestSort('keywords')}
                                        className="hover:text-indigo-700 focus:outline-none flex items-center group"
                                    >
                                        <span className="group-hover:text-indigo-600 transition-colors">Keywords</span>
                                        <span className="text-indigo-500 ml-1">{getSortIndicator('keywords')}</span>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedProducts.length === 0 ? (
                                <tr>
                                    <td className="px-6 py-4 text-gray-500" colSpan="7">No products found</td>
                                </tr>
                            ) : (
                                paginatedProducts.map((p) => {
                                    console.log(' Rendering row for:', p.shopifyId);
                                    const currentPeriod = dateRange;
                                    let views = parseInt(p.analytics?.[currentPeriod]?.views || "0", 10);
                                    let ctr = parseFloat(p.analytics?.[currentPeriod]?.ctr || 0);
                                    console.log(`Row Data for main table - views: ${views}, ctr: ${ctr}`);

                                    const underperforming = views < 20 && ctr < 0.01;
                                    return (
                                        <tr key={p.id} className={underperforming ? 'bg-red-50' : ''}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-3"
                                                        checked={selectedProducts.includes(p.shopifyId)}
                                                        onChange={() => toggleProductSelection(p.shopifyId)}
                                                    />
                                                    {p.title?.replace(/^"|"$/g, '')}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">{p.images || 0}</td>
                                            <td className="px-6 py-4">{p.altTextCount || 0}/{p.images || 0}</td>
                                            <td className="px-6 py-4">{typeof p.seoScore === 'number' ? `${p.seoScore}/100` : '—'}</td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">{formatNumber(views)}</div>
                                                {p.analytics?.[analyticsPeriodKey]?.trend && (
                                                    <div className={`text-sm ${p.analytics[analyticsPeriodKey].trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                        {p.analytics[analyticsPeriodKey].trend > 0 ? '+' : ''}{p.analytics[analyticsPeriodKey].trend}%
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">{(ctr * 100).toFixed(2)}%</div>
                                                {p.analytics?.[analyticsPeriodKey]?.ctrTrend && (
                                                    <div className={`text-sm ${p.analytics[analyticsPeriodKey].ctrTrend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                        {p.analytics[analyticsPeriodKey].ctrTrend > 0 ? '+' : ''}{p.analytics[analyticsPeriodKey].ctrTrend}%
                                                    </div>
                                                )}
                                                {underperforming && (
                                                    <span className="ml-1 text-red-500 font-semibold">↓</span>
                                                )}
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">{p.analytics?.[analyticsPeriodKey]?.keywords?.length || 0}</div>
                                                {p.analytics?.[analyticsPeriodKey]?.keywords?.length > 0 && (
                                                    <button
                                                        className="text-indigo-600 hover:text-indigo-900 text-sm"
                                                        onClick={() => handleViewKeywords(p, analyticsPeriodKey)}
                                                    >
                                                        View keywords
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <div className="text-sm text-gray-600">
                            Page {currentPage} of {Math.ceil(sortedProducts.length / itemsPerPage)}
                        </div>
                        <div className="space-x-2">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() =>
                                    setCurrentPage((prev) =>
                                        Math.min(prev + 1, Math.ceil(sortedProducts.length / itemsPerPage))
                                    )
                                }
                                disabled={currentPage === Math.ceil(sortedProducts.length / itemsPerPage)}
                                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-800">Top Performing Products</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store</th>
                                <th onClick={() => requestSortTop('views')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Traffic {sortConfigTop.key === 'views' ? (sortConfigTop.direction === 'ascending' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => requestSortTop('ctr')} className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    CTR {sortIndicatorTop('ctr')}{sortConfigTop.key === 'ctr' ? (sortConfigTop.direction === 'ascending' ? '↑' : '↓') : ''}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedTopProducts.map((p) => {
                                const metrics = p.analytics?.[analyticsPeriodKey];
                                const views = parseInt(metrics?.views || 0, 10);
                                const ctr = parseFloat(metrics?.ctr || 0);
                                console.log("Row Data Top", views, ctr)
                                const keywords = metrics?.keywords?.length || 0;

                                return (
                                    <tr key={p.id}>
                                        <td className="px-6 py-4">{p.title?.replace(/^"|"$/g, '')}</td>
                                        <td className="px-6 py-4">{p.storeName || '—'}</td>
                                        <td className="px-6 py-4">{formatNumber(views)}</td>
                                        <td className="px-6 py-4">{(ctr * 100).toFixed(2)}%</td>
                                        <td className="px-6 py-4">
                                            {metrics?.trend != null ? (
                                                <span className={metrics.trend >= 0 ? 'text-green-500' : 'text-red-500'}>
                                                    {metrics.trend >= 0 ? '+' : ''}
                                                    {metrics.trend}%
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-6 py-4">{keywords}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <div className="text-sm text-gray-600">
                            Page {currentPageTop} of {Math.ceil(topProducts.length / itemsPerPageTop)}
                        </div>
                        <div className="space-x-2">
                            <button
                                onClick={handlePrevPageTop}
                                disabled={currentPageTop === 1}
                                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={handleNextPageTop}
                                disabled={currentPageTop === Math.ceil(topProducts.length / itemsPerPageTop)}
                                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <button className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">View all products</button>
                </div>
            </div>

            <section className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">Underperforming Products</h2>
                    {selectedProducts.length > 0 && (
                        <div className="relative inline-block text-left">
                            <div>
                                <button
                                    type="button"
                                    id="bulk-actions-menu"
                                    className="inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                    aria-expanded={bulkActionMenuOpen}
                                    aria-haspopup="true"
                                    onClick={toggleBulkActionMenu}
                                >
                                    Bulk Actions ({selectedProducts.length})
                                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>

                            {bulkActionMenuOpen && (
                                <div
                                    id="bulk-actions-dropdown"
                                    className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="bulk-actions-menu"
                                >
                                    <div className="py-1" role="none">
                                        <button onClick={() => handleBulkAction("Enrich Metafields")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Metafields</button>
                                        <button onClick={() => handleBulkAction("Enrich SEO Metadata")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich SEO Metadata</button>
                                        <button onClick={() => handleBulkAction("Enrich Alt Image Text")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Alt Image Text</button>
                                        <button onClick={() => handleBulkAction("Enrich Everything")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Enrich Everything</button>
                                        <button onClick={() => handleBulkAction("Categorize Products")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Categorize Products</button>
                                        <button onClick={() => handleBulkAction("Compress Images")} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">Compress Images</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg shadow border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase text-left">
                            <tr>
                                <th className="px-6 py-3">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                                            checked={selectedProducts.length === products.length && products.length > 0}
                                            onChange={toggleSelectAll}
                                        />
                                        Product
                                    </div>
                                </th>
                                <th className="px-6 py-3">Store</th>
                                <th className="px-6 py-3">Issue</th>
                                <th
                                    onClick={() => requestSortUnder('ctr')}
                                    className="cursor-pointer px-6 py-3"
                                >
                                    Current CTR {sortIndicatorUnder('ctr')}
                                </th>
                                <th className="px-6 py-3">Views</th>
                                <th
                                    onClick={() => requestSortUnder('avgCtr')}
                                    className="cursor-pointer px-6 py-3"
                                >
                                    Average CTR {sortIndicatorUnder('avgCtr')}
                                </th>
                                <th className="px-6 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100 text-sm text-gray-700">
                            {paginatedUnderperformers.length === 0 ? (
                                <tr className="text-center">
                                    <td colSpan="7" className="px-6 py-4 text-gray-400">
                                        No underperforming products found.
                                    </td>
                                </tr>
                            ) : (
                                paginatedUnderperformers
                                    .sort((a, b) => {
                                        const metricA = a.analytics?.[analyticsPeriodKey];
                                        const metricB = b.analytics?.[analyticsPeriodKey];

                                        let aValue, bValue;

                                        if (sortConfigUnder.key === 'ctr') {
                                            aValue = parseFloat(metricA?.ctr || 0);
                                            bValue = parseFloat(metricB?.ctr || 0);
                                        } else if (sortConfigUnder.key === 'avgCtr') {
                                            aValue = overviewStats?.averageCtr * 100 || 0;
                                            bValue = overviewStats?.averageCtr * 100 || 0;
                                        } else {
                                            return 0;
                                        }

                                        return sortConfigUnder.direction === 'ascending' ? aValue - bValue : bValue - aValue;
                                    })
                                    .map(p => {
                                        const metrics = p.analytics?.[analyticsPeriodKey];
                                        console.log("Underperforming Row Data", metrics?.views, metrics?.ctr);
                                        const views = metrics?.views || 0;
                                        const ctr = metrics?.ctr || 0;
                                        return (
                                            <tr key={p.id}>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-3"
                                                            checked={selectedProducts.includes(p.shopifyId)}
                                                            onChange={() => toggleProductSelection(p.shopifyId)}
                                                        />
                                                        {p.title}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">{p.storeName || '—'}</td>
                                                <td className="px-6 py-4">Low CTR & Views</td>
                                                <td className="px-6 py-4">{(ctr * 100).toFixed(2)}%</td>
                                                <td className="px-6 py-4">{views}</td>
                                                <td className="px-6 py-4">{(overviewStats?.averageCtr * 100).toFixed(2)}%</td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => handleViewKeywords(p, analyticsPeriodKey)}
                                                        className="text-indigo-600 hover:underline"
                                                    >
                                                        View Keywords
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
                    <span>Page 1 of 1</span>
                    <div className="space-x-2">
                        <button className="px-3 py-1 border rounded text-gray-600 hover:bg-gray-100">Previous</button>
                        <button className="px-3 py-1 border rounded text-gray-600 hover:bg-gray-100">Next</button>
                    </div>
                </div>

                <div className="mt-2 text-indigo-600 hover:underline text-sm cursor-pointer">
                    View all underperforming products ({underperformers.length})
                </div>
            </section>
        </>
    );
};
export default ProductAnalyticsPage;